/**
 * Per-tenant tmm-api operations (TMMRSL-0029, H §6.2). Every tenant-console call keeps its
 * method, body, response and errors, and moves from the tenant-console prefix to
 * `/v1/reseller-admin/tenants/{slug}/<suffix>`; the tenant is named in the path and checked live.
 * Wire types are the vendored contract's (`api/openapi.yaml`) — the reseller twins reuse them.
 * The tenant resource itself (`GET` / `PATCH /tenants/{slug}`) lives in `resellerAdmin.ts`.
 */
import type {
  AdminCancelRequest,
  AdminCompRequest,
  AdminDeletionCancelRequest,
  AdminDeletionList,
  AdminDeletionRequest,
  AdminInvitationRequest,
  AdminInvitationResponse,
  AdminSeatList,
  AdminSubscribeRequest,
  AdminSubscribeResponse,
  AdminSubscriptionDetail,
  AdminSubscriptionList,
  AdminSubscriptionRow,
  AdminUncompRequest,
  AcceptRequest,
  AdminWaiveRequest,
  AffiliateAgreementHistory,
  AffiliateAgreementView,
  AffiliateAgreementWriteRequest,
  AffiliateCodeCreateRequest,
  AffiliateCodeLabelRequest,
  AffiliateEarnings,
  AffiliateCodeList,
  AffiliateCodeView,
  AffiliateDetail,
  AffiliateInviteRequest,
  AffiliateList,
  AffiliatePatchRequest,
  AffiliateReferralList,
  AffiliateReferralRow,
  AffiliateSettingsView,
  AffiliateSettingsWriteRequest,
  ApplyPromotionRequest,
  CancelResponse,
  CardReplaceRequest,
  ChargeView,
  ChangeOfferRequest,
  ContactSyncView,
  CrmStatusView,
  DeletionRequestView,
  DomainHelpQuestionRequest,
  DomainHelpQuestionResponse,
  DomainHelpRequest,
  DomainHelpResponse,
  OfferList,
  PromotionList,
  PromotionPatchRequest,
  PromotionRedemptionList,
  PromotionView,
  PromotionWriteRequest,
  ReferralVoidRequest,
  AdminStatementList,
  StatementView,
  TenantAddonList,
  TenantAddonView,
  TenantDashboard,
  ResellerAiUsageReport,
} from './generated';
import { request } from './client';

/** `/v1/reseller-admin/tenants/{slug}/<suffix>` — the one per-tenant path builder (H §6.2). */
export function tenantPath(slug: string, suffix: string): string {
  return `/v1/reseller-admin/tenants/${encodeURIComponent(slug)}/${suffix}`;
}

/**
 * The per-tenant operations bound to one tenant. Screens get it from `useTenantApi()`, which binds
 * the workspace's slug.
 */
export function createTenantApi(slug: string) {
  return {
    listStatements(params?: { period?: string; status?: string }): Promise<AdminStatementList> {
      const query: Record<string, string> = {};
      if (params?.period !== undefined) query.period = params.period;
      if (params?.status !== undefined) query.status = params.status;
      return request<AdminStatementList>(
        'GET',
        tenantPath(slug, 'statements'),
        Object.keys(query).length > 0 ? { query } : {},
      );
    },

    getAiUsage(params?: {
      from?: string;
      to?: string;
      userId?: number;
    }): Promise<ResellerAiUsageReport> {
      const query: Record<string, string> = {};
      if (params?.from !== undefined) query.from = params.from;
      if (params?.to !== undefined) query.to = params.to;
      if (params?.userId !== undefined) query.userId = String(params.userId);
      return request<ResellerAiUsageReport>(
        'GET',
        tenantPath(slug, 'ai-usage'),
        Object.keys(query).length > 0 ? { query } : {},
      );
    },

    listDeletionRequests(): Promise<AdminDeletionList> {
      return request<AdminDeletionList>('GET', tenantPath(slug, 'deletion-requests'));
    },

    requestDeletion(body: AdminDeletionRequest): Promise<DeletionRequestView> {
      return request<DeletionRequestView>('POST', tenantPath(slug, 'deletion-requests'), {
        json: body,
      });
    },

    cancelDeletion(id: number, body?: AdminDeletionCancelRequest): Promise<DeletionRequestView> {
      return request<DeletionRequestView>(
        'POST',
        tenantPath(slug, `deletion-requests/${id}/cancel`),
        {
          json: body ?? {},
        },
      );
    },

    getCrm(): Promise<ContactSyncView> {
      return request<ContactSyncView>('GET', tenantPath(slug, 'crm'));
    },

    resyncCrm(): Promise<CrmStatusView> {
      return request<CrmStatusView>('POST', tenantPath(slug, 'crm/resync'));
    },

    getDashboard(params?: { from?: string; to?: string }): Promise<TenantDashboard> {
      const query: Record<string, string> = {};
      if (params?.from !== undefined) query.from = params.from;
      if (params?.to !== undefined) query.to = params.to;
      return request<TenantDashboard>(
        'GET',
        tenantPath(slug, 'dashboard'),
        Object.keys(query).length > 0 ? { query } : {},
      );
    },

    getOffers(): Promise<OfferList> {
      return request<OfferList>('GET', tenantPath(slug, 'offers'));
    },

    listSubscriptions(params?: {
      status?: string;
      email?: string;
    }): Promise<AdminSubscriptionList> {
      const query: Record<string, string> = {};
      if (params?.status !== undefined) query.status = params.status;
      if (params?.email !== undefined) query.email = params.email;
      return request<AdminSubscriptionList>(
        'GET',
        tenantPath(slug, 'subscriptions'),
        Object.keys(query).length > 0 ? { query } : {},
      );
    },

    getSubscription(id: number): Promise<AdminSubscriptionDetail> {
      return request<AdminSubscriptionDetail>('GET', tenantPath(slug, `subscriptions/${id}`));
    },

    subscribe(body: AdminSubscribeRequest): Promise<AdminSubscribeResponse> {
      return request<AdminSubscribeResponse>('POST', tenantPath(slug, 'subscriptions'), {
        json: body,
      });
    },

    cancelSubscription(id: number, body: AdminCancelRequest): Promise<CancelResponse> {
      return request<CancelResponse>('POST', tenantPath(slug, `subscriptions/${id}/cancel`), {
        json: body,
      });
    },

    comp(id: number, body: AdminCompRequest): Promise<AdminSubscriptionRow> {
      return request<AdminSubscriptionRow>('POST', tenantPath(slug, `subscriptions/${id}/comp`), {
        json: body,
      });
    },

    uncomp(id: number, body: AdminUncompRequest): Promise<AdminSubscriptionRow> {
      return request<AdminSubscriptionRow>('POST', tenantPath(slug, `subscriptions/${id}/uncomp`), {
        json: body,
      });
    },

    changeOffer(id: number, body: ChangeOfferRequest): Promise<AdminSubscriptionRow> {
      return request<AdminSubscriptionRow>(
        'POST',
        tenantPath(slug, `subscriptions/${id}/change-offer`),
        { json: body },
      );
    },

    applyPromotion(id: number, body: ApplyPromotionRequest): Promise<AdminSubscriptionRow> {
      return request<AdminSubscriptionRow>(
        'POST',
        tenantPath(slug, `subscriptions/${id}/apply-promotion`),
        { json: body },
      );
    },

    endSubscriptionPromotion(id: number): Promise<AdminSubscriptionRow> {
      return request<AdminSubscriptionRow>(
        'POST',
        tenantPath(slug, `subscriptions/${id}/end-promotion`),
      );
    },

    invite(body: AdminInvitationRequest): Promise<AdminInvitationResponse> {
      return request<AdminInvitationResponse>('POST', tenantPath(slug, 'invitations'), {
        json: body,
      });
    },

    retryStatement(id: number): Promise<StatementView> {
      return request<StatementView>('POST', tenantPath(slug, `statements/${id}/retry`));
    },

    waiveStatement(id: number, body: AdminWaiveRequest): Promise<StatementView> {
      return request<StatementView>('POST', tenantPath(slug, `statements/${id}/waive`), {
        json: body,
      });
    },

    listPromotions(): Promise<PromotionList> {
      return request<PromotionList>('GET', tenantPath(slug, 'billing/promotions'));
    },

    createPromotion(body: PromotionWriteRequest): Promise<PromotionView> {
      return request<PromotionView>('POST', tenantPath(slug, 'billing/promotions'), { json: body });
    },

    patchPromotion(id: number, body: PromotionPatchRequest): Promise<PromotionView> {
      return request<PromotionView>('PATCH', tenantPath(slug, `billing/promotions/${id}`), {
        json: body,
      });
    },

    pausePromotion(id: number): Promise<PromotionView> {
      return request<PromotionView>('POST', tenantPath(slug, `billing/promotions/${id}/pause`));
    },

    endPromotion(id: number): Promise<PromotionView> {
      return request<PromotionView>('POST', tenantPath(slug, `billing/promotions/${id}/end`));
    },

    listRedemptions(id: number): Promise<PromotionRedemptionList> {
      return request<PromotionRedemptionList>(
        'GET',
        tenantPath(slug, `billing/promotions/${id}/redemptions`),
      );
    },

    listSeats(params?: { sponsorUserId?: number }): Promise<AdminSeatList> {
      const query: Record<string, string> = {};
      if (params?.sponsorUserId !== undefined) query.sponsorUserId = String(params.sponsorUserId);
      return request<AdminSeatList>(
        'GET',
        tenantPath(slug, 'seats'),
        Object.keys(query).length > 0 ? { query } : {},
      );
    },

    listAffiliates(): Promise<AffiliateList> {
      return request<AffiliateList>('GET', tenantPath(slug, 'affiliates'));
    },

    inviteAffiliate(body: AffiliateInviteRequest): Promise<AffiliateDetail> {
      return request<AffiliateDetail>('POST', tenantPath(slug, 'affiliates'), { json: body });
    },

    getAffiliate(id: number): Promise<AffiliateDetail> {
      return request<AffiliateDetail>('GET', tenantPath(slug, `affiliates/${id}`));
    },

    patchAffiliate(id: number, body: AffiliatePatchRequest): Promise<AffiliateDetail> {
      return request<AffiliateDetail>('PATCH', tenantPath(slug, `affiliates/${id}`), {
        json: body,
      });
    },

    approveAffiliate(id: number): Promise<AffiliateDetail> {
      return request<AffiliateDetail>('POST', tenantPath(slug, `affiliates/${id}/approve`));
    },

    pauseAffiliate(id: number): Promise<AffiliateDetail> {
      return request<AffiliateDetail>('POST', tenantPath(slug, `affiliates/${id}/pause`));
    },

    resumeAffiliate(id: number): Promise<AffiliateDetail> {
      return request<AffiliateDetail>('POST', tenantPath(slug, `affiliates/${id}/resume`));
    },

    endAffiliate(id: number): Promise<AffiliateDetail> {
      return request<AffiliateDetail>('POST', tenantPath(slug, `affiliates/${id}/end`));
    },

    getAffiliateAgreement(id: number): Promise<AffiliateAgreementHistory> {
      return request<AffiliateAgreementHistory>(
        'GET',
        tenantPath(slug, `affiliates/${id}/agreement`),
      );
    },

    /**
     * The deal names a commission plan (TMMAPI-0104). The agreement body carries the plan id and
     * payout terms only.
     */
    putAffiliateAgreement(
      id: number,
      body: AffiliateAgreementWriteRequest,
    ): Promise<AffiliateAgreementView> {
      return request<AffiliateAgreementView>(
        'PUT',
        tenantPath(slug, `affiliates/${id}/agreement`),
        {
          json: body,
        },
      );
    },

    /**
     * An affiliate's generated earnings by month (TMMRSL-0051). The body is
     * normalized by `normalizeAffiliateEarnings` (`features/affiliates/affiliateEarnings.ts`).
     */
    getAffiliateEarnings(id: number): Promise<AffiliateEarnings> {
      return request<AffiliateEarnings>('GET', tenantPath(slug, `affiliates/${id}/earnings`));
    },

    listAffiliateCodes(id: number): Promise<AffiliateCodeList> {
      return request<AffiliateCodeList>('GET', tenantPath(slug, `affiliates/${id}/codes`));
    },

    createAffiliateCode(id: number, body: AffiliateCodeCreateRequest): Promise<AffiliateCodeView> {
      return request<AffiliateCodeView>('POST', tenantPath(slug, `affiliates/${id}/codes`), {
        json: body,
      });
    },

    patchAffiliateCode(
      id: number,
      codeId: number,
      body: AffiliateCodeLabelRequest,
    ): Promise<AffiliateCodeView> {
      return request<AffiliateCodeView>(
        'PATCH',
        tenantPath(slug, `affiliates/${id}/codes/${codeId}`),
        { json: body },
      );
    },

    pauseAffiliateCode(id: number, codeId: number): Promise<AffiliateCodeView> {
      return request<AffiliateCodeView>(
        'POST',
        tenantPath(slug, `affiliates/${id}/codes/${codeId}/pause`),
      );
    },

    resumeAffiliateCode(id: number, codeId: number): Promise<AffiliateCodeView> {
      return request<AffiliateCodeView>(
        'POST',
        tenantPath(slug, `affiliates/${id}/codes/${codeId}/resume`),
      );
    },

    listAffiliateReferrals(id: number): Promise<AffiliateReferralList> {
      return request<AffiliateReferralList>('GET', tenantPath(slug, `affiliates/${id}/referrals`));
    },

    voidReferral(attributionId: number, body: ReferralVoidRequest): Promise<AffiliateReferralRow> {
      return request<AffiliateReferralRow>(
        'POST',
        tenantPath(slug, `referrals/${attributionId}/void`),
        { json: body },
      );
    },

    getAffiliateSettings(): Promise<AffiliateSettingsView> {
      return request<AffiliateSettingsView>('GET', tenantPath(slug, 'affiliate-settings'));
    },

    putAffiliateSettings(body: AffiliateSettingsWriteRequest): Promise<AffiliateSettingsView> {
      return request<AffiliateSettingsView>('PUT', tenantPath(slug, 'affiliate-settings'), {
        json: body,
      });
    },

    listAddons(): Promise<TenantAddonList> {
      return request<TenantAddonList>('GET', tenantPath(slug, 'addons'));
    },

    acceptAddon(id: number, body: AcceptRequest): Promise<TenantAddonView> {
      return request<TenantAddonView>('POST', tenantPath(slug, `addons/${id}/accept`), {
        json: body,
      });
    },

    declineAddon(id: number): Promise<TenantAddonView> {
      return request<TenantAddonView>('POST', tenantPath(slug, `addons/${id}/decline`));
    },

    cancelAddon(id: number): Promise<TenantAddonView> {
      return request<TenantAddonView>('POST', tenantPath(slug, `addons/${id}/cancel`));
    },

    replaceAddonCard(id: number, body: CardReplaceRequest): Promise<TenantAddonView> {
      return request<TenantAddonView>('PUT', tenantPath(slug, `addons/${id}/card`), { json: body });
    },

    retryAddonCharge(id: number, chargeId: number): Promise<ChargeView> {
      return request<ChargeView>(
        'POST',
        tenantPath(slug, `addons/${id}/charges/${chargeId}/retry`),
      );
    },

    startDomainHelp(body: DomainHelpRequest): Promise<DomainHelpResponse> {
      return request<DomainHelpResponse>('POST', tenantPath(slug, 'domain-help'), { json: body });
    },

    getDomainHelp(id: number): Promise<DomainHelpResponse> {
      return request<DomainHelpResponse>('GET', tenantPath(slug, `domain-help/${id}`));
    },

    askDomainHelp(
      id: number,
      body: DomainHelpQuestionRequest,
    ): Promise<DomainHelpQuestionResponse> {
      return request<DomainHelpQuestionResponse>(
        'POST',
        tenantPath(slug, `domain-help/${id}/questions`),
        { json: body },
      );
    },
  };
}

export type TenantApi = ReturnType<typeof createTenantApi>;
